// 잿불 협곡 배치: 판정 격자에서 경계를 뽑는다. 렌더 전용 높이만 바꾸며 보행면은 0m다.
import { Cell, cellAt, gateAt, lavaAt, type MapDef, type MapGrid } from '../../shared/levels';
import { WAVE_SPAWN } from '../../shared/config';
import type { EnvLayout, LayoutOptions, Placement, KitKind } from './layout';
import { mulberry32 } from './rng';

export const CHASM_DEPTH = 8;
export const BASALT_LIP = 0.4;
export interface EmberEdge { x: number; z: number; nx: number; nz: number; chasm: boolean }
export function chasmAt(map: MapDef, grid: MapGrid, x: number, z: number): boolean {
  return cellAt(grid, x, z) === Cell.Void && !!map.chasms?.some(r => x >= r.x0 && x < r.x1 && z >= r.z0 && z < r.z1);
}
export function emberEdges(map: MapDef, grid: MapGrid): EmberEdge[] {
  const edges: EmberEdge[] = [];
  const directions = [[0, -1], [0, 1], [-1, 0], [1, 0]];
  for (let z = grid.originZ; z < grid.originZ + grid.height; z++) for (let x = grid.originX; x < grid.originX + grid.width; x++) {
    if (cellAt(grid, x + 0.5, z + 0.5) === Cell.Void) continue;
    for (const [dx, dz] of directions) {
      const vx = x + 0.5 + dx, vz = z + 0.5 + dz;
      if (cellAt(grid, vx, vz) !== Cell.Void) continue;
      edges.push({ x: x + 0.5 + dx * 0.5, z: z + 0.5 + dz * 0.5, nx: -dx, nz: -dz, chasm: chasmAt(map, grid, vx, vz) });
    }
  }
  return edges;
}
/** 장식의 회전된 발자국을 감싸는 반경. 모든 장식은 축별 크기를 명시한다. */
export function emberDecorRadius(p: Placement): number { return Math.hypot(p.w, p.d) / 2; }
function rectDistance(x: number, z: number, x0: number, z0: number, x1: number, z1: number): number {
  return Math.hypot(Math.max(x0 - x, 0, x - x1), Math.max(z0 - z, 0, z - z1));
}
export function emberDecorFree(map: MapDef, grid: MapGrid, x: number, z: number, radius: number): boolean {
  if (radius <= 0) return false;
  // 모서리 9점만 찍으면 큰 소품 아래의 1m 위험 셀을 건너뛸 수 있으므로 겹친 셀 전부 검사한다.
  for (let cz = Math.floor(z - radius); cz <= Math.floor(z + radius); cz++) for (let cx = Math.floor(x - radius); cx <= Math.floor(x + radius); cx++) {
    if (cellAt(grid, cx + 0.5, cz + 0.5) !== Cell.Floor || lavaAt(grid, cx + 0.5, cz + 0.5) || gateAt(grid, cx + 0.5, cz + 0.5) >= 0) return false;
  }
  if (map.gates.some(g => rectDistance(x, z, g.rect.x0, g.rect.z0, g.rect.x1, g.rect.z1) < radius + 2)) return false;
  if (Math.hypot(x - map.spawn.x, z - map.spawn.z) < radius + 4) return false;
  if (map.vents?.some(v => Math.hypot(x - v.x, z - v.z) < v.radius + radius + 0.3)) return false;
  for (const room of map.rooms) {
    const a = room.area, b = room.backline, boss = room.bossAt;
    if (b && rectDistance(x, z, b.x0, b.z0, b.x1, b.z1) < radius + 0.8) return false;
    if (boss && Math.hypot(x - boss.x, z - boss.z) < radius + 4) return false;
    const ins = WAVE_SPAWN.inset, x0 = a.x0 + ins, x1 = a.x1 - ins, z0 = a.z0 + ins, z1 = a.z1 - ins;
    // 웨이브 입구 후보 둘레 전체와 흩어짐, 최대 몹 발자국을 비워 시드와 무관하게 보호한다.
    const entryDistance = Math.min(rectDistance(x, z, x0, z0, x1, z0), rectDistance(x, z, x0, z1, x1, z1),
      rectDistance(x, z, x0, z0, x0, z1), rectDistance(x, z, x1, z0, x1, z1));
    if (entryDistance < radius + WAVE_SPAWN.jitter + 0.85) return false;
    if (x >= a.x0 && x <= a.x1 && z >= a.z0 && z <= a.z1) {
      // 방 중앙 종축, 문 연결축과 두 다리 폭은 장식 없이 남긴다.
      if (Math.abs(x - (a.x0 + a.x1) / 2) < radius + 3) return false;
      for (const gi of [room.gateIn, room.gateOut]) {
        const g = map.gates[gi];
        if (g && Math.abs(x - (g.rect.x0 + g.rect.x1) / 2) < radius + 2) return false;
      }
      if (room === map.rooms[0] && Math.abs(Math.abs(x) - 10.5) < radius + 2) return false;
    }
  }
  return true;
}
export function layoutEmber(map: MapDef, grid: MapGrid, opt: LayoutOptions): EnvLayout {
  const rnd = mulberry32(opt.seed);
  const placements: Placement[] = [], flames: EnvLayout['flames'] = [];
  const put = (kind: KitKind, x: number, z: number, h: number, w: number, d: number, landmark = -1, rot = 0): Placement => {
    const p: Placement = { kind, x, y: 0, z, h, w, d, landmark, rot, tint: 0xd2cbd0, cut: 1 };
    placements.push(p); return p;
  };
  for (const e of emberEdges(map, grid)) {
    // 절벽 키트는 빈칸 쪽에만, 협곡은 절차 절벽과 낮은 턱이 맡는다.
    if (e.chasm) continue;
    put('wall', e.x - e.nx * 0.4, e.z - e.nz * 0.4, map.wallHeight + rnd() * 2.2, 1.04 + rnd() * 0.38, 0.8 + rnd() * 0.3, -1, Math.atan2(e.nx, e.nz));
  }
  map.landmarks.forEach((l, i) => {
    const x = (l.rect.x0 + l.rect.x1) / 2, z = (l.rect.z0 + l.rect.z1) / 2;
    // 현무암은 잎·이끼가 없는 바위 키트를 축별로 늘려 주상절리 실루엣으로 쓴다.
    const kind = l.kind === 'rubble' ? 'rubble' : l.kind === 'basalt' ? 'rock' : 'statue';
    put(kind, x, z, l.height, l.rect.x1 - l.rect.x0, l.rect.z1 - l.rect.z0, i, rnd() * Math.PI);
  });
  for (const g of map.gates) {
    const r = g.rect;
    put('arch', (r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2, 5.4, r.x1 - r.x0 + 2, 1.2);
  }
  // 다리 양측·후열의 기존 충돌 현무암 위에 표식을 얹는다. 스폰 가능한 바닥을 차지하지 않는다.
  map.landmarks.forEach((l, i) => {
    if (l.kind !== 'basalt') return;
    const x = (l.rect.x0 + l.rect.x1) / 2, z = (l.rect.z0 + l.rect.z1) / 2;
    const p = put(i % 2 ? 'brazier' : 'banner', x, z, i % 2 ? 0.9 : 2.0, 0.7, 0.7, i);
    p.y = l.height; p.part = i;
    if (p.kind === 'brazier') flames.push({ x, y: p.y + 0.86, z, s: 0.65, v: rnd() });
  });
  const kinds: KitKind[] = ['rock_lo', 'pillar_broken', 'wall_broken', 'crates', 'stairs', 'rubble', 'rock', 'brazier', 'banner'];
  let serial = 0;
  for (const room of map.rooms) {
    const r = room.area;
    let count = 0;
    for (let i = 0; i < 700 && count < 18 * opt.density; i++) {
      const x = r.x0 + 1 + rnd() * (r.x1 - r.x0 - 2), z = r.z0 + 1 + rnd() * (r.z1 - r.z0 - 2);
      const w = 0.8 + rnd() * 0.35, d = 0.7 + rnd() * 0.3, radius = Math.hypot(w, d) / 2;
      if (Math.min(x - r.x0, r.x1 - x, z - r.z0, r.z1 - z) > 7 || !emberDecorFree(map, grid, x, z, radius)) continue;
      if (placements.some(p => p.landmark < 0 && p.kind !== 'wall' && p.kind !== 'arch' && Math.hypot(x - p.x, z - p.z) < radius + emberDecorRadius(p) + 1)) continue;
      const kind = kinds[serial++ % kinds.length];
      const h = kind === 'banner' ? 2.3 : kind === 'pillar_broken' ? 1.6 : kind === 'wall_broken' ? 1.1 : kind === 'brazier' ? 1.2 : 0.5;
      put(kind, x, z, h, w, d, -1, rnd() * 6.28);
      if (kind === 'brazier') flames.push({ x, y: 1.15, z, s: 0.7, v: rnd() });
      count++;
    }
  }
  return { placements, flames, glows: [], tufts: [], flowers: [], shafts: [], pools: [], lamps: [], ivy: [],
    plateau: { originX: grid.originX, originZ: grid.originZ, nx: 2, nz: 2, f: new Float32Array(4).fill(-1) },
    shade: { originX: grid.originX, originZ: grid.originZ, width: grid.width, height: grid.height, data: new Uint8Array(grid.cells.length) } };
}
